import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const ONBOARDING_KEY = 'generational_bridge_onboarding';

const defaultState = {
  /** First-time walkthrough seen (spec: walkthrough_seen) — persisted in localStorage. */
  hasSeenWelcome: false,
  hasCreatedFirstTree: false,
  hasAddedFirstPerson: false,
  hasAddedSecondPerson: false,
  hasRecordedFirstStory: false,
  hasInviteSent: false,
  inviteStepDismissed: false,
  dismissedHints: [],
  /** Tree page: user minimized the setup checklist (restorable). */
  treeChecklistCollapsed: false,
  /** Dashboard: checklist collapsed to a thin strip (persisted). */
  dashboardChecklistMinimized: false,
  /** Dashboard: full checklist hidden — reopen via “Setup progress” (persisted). */
  dashboardChecklistDismissed: false,
  /** Increment to reopen welcome walkthrough from Guide menu. */
  walkthroughReplayNonce: 0,
};

const OnboardingContext = createContext(null);

export const OnboardingProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(ONBOARDING_KEY);
      return saved ? { ...defaultState, ...JSON.parse(saved) } : defaultState;
    } catch {
      return defaultState;
    }
  });

  useEffect(() => {
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify(state));
  }, [state]);

  const markWelcomeSeen = useCallback(() => {
    setState((prev) => (prev.hasSeenWelcome ? prev : { ...prev, hasSeenWelcome: true }));
  }, []);

  const markFirstTreeCreated = useCallback(() => {
    setState((prev) => (prev.hasCreatedFirstTree ? prev : { ...prev, hasCreatedFirstTree: true }));
  }, []);

  const markFirstPersonAdded = useCallback(() => {
    setState((prev) => (prev.hasAddedFirstPerson ? prev : { ...prev, hasAddedFirstPerson: true }));
  }, []);

  const markSecondPersonAdded = useCallback(() => {
    setState((prev) => (prev.hasAddedSecondPerson ? prev : { ...prev, hasAddedSecondPerson: true }));
  }, []);

  const markFirstStoryRecorded = useCallback(() => {
    setState((prev) => (prev.hasRecordedFirstStory ? prev : { ...prev, hasRecordedFirstStory: true }));
  }, []);

  const markInviteSent = useCallback(() => {
    setState((prev) => (prev.hasInviteSent ? prev : { ...prev, hasInviteSent: true }));
  }, []);

  const dismissInviteStep = useCallback(() => {
    setState((prev) => (prev.inviteStepDismissed ? prev : { ...prev, inviteStepDismissed: true }));
  }, []);

  const setTreeChecklistCollapsed = useCallback((collapsed) => {
    setState((prev) =>
      prev.treeChecklistCollapsed === Boolean(collapsed) ? prev : { ...prev, treeChecklistCollapsed: Boolean(collapsed) }
    );
  }, []);

  const toggleTreeChecklistCollapsed = useCallback(() => {
    setState((prev) => ({ ...prev, treeChecklistCollapsed: !prev.treeChecklistCollapsed }));
  }, []);

  const setDashboardChecklistMinimized = useCallback((minimized) => {
    setState((prev) =>
      prev.dashboardChecklistMinimized === Boolean(minimized)
        ? prev
        : { ...prev, dashboardChecklistMinimized: Boolean(minimized) }
    );
  }, []);

  const setDashboardChecklistDismissed = useCallback((dismissed) => {
    setState((prev) =>
      prev.dashboardChecklistDismissed === Boolean(dismissed)
        ? prev
        : { ...prev, dashboardChecklistDismissed: Boolean(dismissed) }
    );
  }, []);

  const reopenDashboardChecklist = useCallback(() => {
    setState((prev) =>
      prev.dashboardChecklistDismissed === false && prev.dashboardChecklistMinimized === false
        ? prev
        : { ...prev, dashboardChecklistDismissed: false, dashboardChecklistMinimized: false }
    );
  }, []);

  const requestWalkthroughReplay = useCallback(() => {
    setState((prev) => ({ ...prev, walkthroughReplayNonce: (prev.walkthroughReplayNonce || 0) + 1 }));
  }, []);

  const dismissHint = useCallback((hintId) => {
    setState((prev) =>
      prev.dismissedHints.includes(hintId) ? prev : { ...prev, dismissedHints: [...prev.dismissedHints, hintId] }
    );
  }, []);

  const isHintDismissed = (hintId) => {
    return state.dismissedHints.includes(hintId);
  };

  const resetOnboarding = () => {
    setState(defaultState);
  };

  const needsWelcome = !state.hasSeenWelcome;
  const needsFirstTreeHint = !state.hasCreatedFirstTree && !isHintDismissed('create-tree');
  const needsFirstPersonHint = state.hasCreatedFirstTree && !state.hasAddedFirstPerson && !isHintDismissed('add-person');
  const needsAddParentHint =
    state.hasAddedFirstPerson && !state.hasAddedSecondPerson && !isHintDismissed('add-parent');

  /** Core MVP path: tree → first person → first memory (account implied when using the app). */
  const isMvpCoreComplete = useMemo(
    () =>
      state.hasCreatedFirstTree && state.hasAddedFirstPerson && state.hasRecordedFirstStory,
    [state.hasCreatedFirstTree, state.hasAddedFirstPerson, state.hasRecordedFirstStory]
  );

  /** Optional invite step resolved — does not block core completion. */
  const inviteStepResolved = state.hasInviteSent || state.inviteStepDismissed;

  /** Hide checklist when core done and user invited or dismissed invite step. */
  const isOnboardingFullyDone = isMvpCoreComplete && inviteStepResolved;

  /** Legacy: sessions that completed the old “second person” requirement. */
  const isFirstSessionComplete =
    state.hasCreatedFirstTree &&
    state.hasAddedFirstPerson &&
    state.hasAddedSecondPerson &&
    state.hasRecordedFirstStory;

  return (
    <OnboardingContext.Provider
      value={{
        ...state,
        needsWelcome,
        needsFirstTreeHint,
        needsFirstPersonHint,
        needsAddParentHint,
        isFirstSessionComplete,
        isMvpCoreComplete,
        inviteStepResolved,
        isOnboardingFullyDone,
        markWelcomeSeen,
        markFirstTreeCreated,
        markFirstPersonAdded,
        markSecondPersonAdded,
        markFirstStoryRecorded,
        markInviteSent,
        dismissInviteStep,
        setTreeChecklistCollapsed,
        toggleTreeChecklistCollapsed,
        setDashboardChecklistMinimized,
        setDashboardChecklistDismissed,
        reopenDashboardChecklist,
        requestWalkthroughReplay,
        dismissHint,
        isHintDismissed,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};
